import React, { useState } from 'react';
import { Briefcase, MapPin, Clock, X, ArrowRight } from 'lucide-react';
import { useApp } from '../../context/AppContext';
import { submitJobApplication } from '../../services/publicWrites';
import './PublicPages.css';

export default function CareersPage() {
  const { data, loading } = useApp();
  const [selected, setSelected] = useState(null);
  const [form, setForm] = useState({ name: '', email: '', phone: '', cover: '' });
  const [cvFile, setCvFile] = useState(null);
  const [submitted, setSubmitted] = useState(false);
  const [err, setErr] = useState('');
  const [sending, setSending] = useState(false);

  const openJobs = (data.jobs || []).filter(j => j.status === 'Open');

  const handleApply = async () => {
    if (!form.name || !form.email || !selected) return;
    setErr('');
    setSending(true);
    try {
      await submitJobApplication({
        name: form.name,
        email: form.email,
        phone: form.phone,
        cover: form.cover,
        jobTitle: selected.title,
        jobId: selected.id,
        cvFile,
      });
      setSubmitted(true);
      setTimeout(() => {
        setSubmitted(false);
        setSelected(null);
        setForm({ name: '', email: '', phone: '', cover: '' });
        setCvFile(null);
      }, 2500);
    } catch (e) {
      setErr(e.message || 'Submission failed. Check Firebase Storage rules for CV uploads.');
    }
    setSending(false);
  };

  return (
    <div className="careers-page">
      <div className="careers-hero" style={{ padding: '120px 24px 60px' }}>
        <div className="section-tag">Join Our Team</div>
        <h1 className="section-heading">Careers at Fortitude</h1>
        <p style={{ color: 'var(--text-muted)', maxWidth: 520, margin: '0 auto', fontSize: '1rem', lineHeight: 1.7 }}>
          We're building Africa's premier IT solutions company. Join a passionate team of innovators and help shape the digital future.
        </p>
      </div>

      <div className="careers-jobs">
        <h2 style={{ fontFamily: 'var(--font-display)', fontSize: '1.6rem', fontWeight: 700, marginBottom: 24 }}>
          Open Positions ({loading ? '…' : openJobs.length})
        </h2>

        {!loading && openJobs.length === 0 && (
          <div className="card" style={{ textAlign: 'center', padding: 48, color: 'var(--text-muted)' }}>
            <Briefcase size={36} style={{ marginBottom: 12, color: 'var(--text-dim)' }} />
            <p>No open positions right now. Check back soon!</p>
          </div>
        )}

        {openJobs.map(job => (
          <div key={job.id} className="job-listing">
            <div>
              <h3 className="job-listing-title">{job.title}</h3>
              <div className="job-listing-meta">
                <span style={{ display: 'flex', alignItems: 'center', gap: 4 }}><Briefcase size={12} /> {job.dept}</span>
                <span style={{ display: 'flex', alignItems: 'center', gap: 4 }}><Clock size={12} /> {job.type}</span>
                <span style={{ display: 'flex', alignItems: 'center', gap: 4 }}><MapPin size={12} /> Sandton, Johannesburg</span>
                {job.deadline && <span>Deadline: {new Date(job.deadline).toLocaleDateString('en-ZA')}</span>}
              </div>
            </div>
            <button className="btn-gold" onClick={() => setSelected(job)} style={{ display: 'flex', alignItems: 'center', gap: 8, flexShrink: 0 }}>
              Apply Now <ArrowRight size={14} />
            </button>
          </div>
        ))}

        <div style={{ marginTop: 60 }}>
          <h2 style={{ fontFamily: 'var(--font-display)', fontSize: '1.8rem', fontWeight: 700, marginBottom: 24, textAlign: 'center' }}>
            Why Work With Us?
          </h2>
          <div style={{ display: 'grid', gridTemplateColumns: 'repeat(auto-fit, minmax(220px, 1fr))', gap: 16 }}>
            {[
              { title: 'Growth & Learning', desc: 'Continuous learning opportunities and career development paths.' },
              { title: 'Flexible Work', desc: 'Hybrid working arrangements to support work-life balance.' },
              { title: 'Innovation Culture', desc: 'Freedom to experiment, innovate, and make your mark.' },
              { title: 'Competitive Package', desc: 'Market-related salaries with performance bonuses.' },
            ].map(b => (
              <div key={b.title} className="card" style={{ textAlign: 'center' }}>
                <h4 style={{ fontWeight: 700, marginBottom: 8, color: 'var(--secondary-color)' }}>{b.title}</h4>
                <p style={{ fontSize: '0.875rem', color: 'var(--text-muted)', lineHeight: 1.6 }}>{b.desc}</p>
              </div>
            ))}
          </div>
        </div>
      </div>

      {selected && (
        <div className="modal-overlay" onClick={() => !sending && setSelected(null)}>
          <div className="modal" onClick={e => e.stopPropagation()}>
            <div className="modal-header">
              <div>
                <h2 className="modal-title">Apply for Position</h2>
                <p style={{ fontSize: '0.8rem', color: 'var(--secondary-color)', marginTop: 2 }}>{selected.title}</p>
              </div>
              <button type="button" className="modal-close" onClick={() => !sending && setSelected(null)}><X size={20} /></button>
            </div>

            {submitted ? (
              <div style={{ textAlign: 'center', padding: '40px 20px' }}>
                <div style={{ fontSize: '3rem', marginBottom: 16 }}>✅</div>
                <h3 style={{ fontWeight: 700, marginBottom: 8 }}>Application Submitted!</h3>
                <p style={{ color: 'var(--text-muted)', fontSize: '0.875rem' }}>We'll review your application and get back to you within 5 business days.</p>
              </div>
            ) : (
              <>
                {err && <p style={{ color: 'var(--danger)', fontSize: '0.85rem', marginBottom: 12 }}>{err}</p>}
                <div className="modal-form">
                  <div className="form-group">
                    <label className="form-label">Full Name *</label>
                    <input className="form-input" placeholder="Your full name" value={form.name} onChange={e => setForm({...form, name: e.target.value})} />
                  </div>
                  <div className="form-row">
                    <div className="form-group">
                      <label className="form-label">Email *</label>
                      <input className="form-input" type="email" placeholder="your@email.com" value={form.email} onChange={e => setForm({...form, email: e.target.value})} />
                    </div>
                    <div className="form-group">
                      <label className="form-label">Phone</label>
                      <input className="form-input" placeholder="+27 ..." value={form.phone} onChange={e => setForm({...form, phone: e.target.value})} />
                    </div>
                  </div>
                  <div className="form-group">
                    <label className="form-label">Cover Letter</label>
                    <textarea className="form-input" rows={4} placeholder="Tell us why you'd be a great fit..." value={form.cover} onChange={e => setForm({...form, cover: e.target.value})} />
                  </div>
                  <div className="form-group">
                    <label className="form-label">Upload CV (PDF / Word)</label>
                    <input
                      type="file"
                      accept=".pdf,.doc,.docx,application/pdf,application/msword,application/vnd.openxmlformats-officedocument.wordprocessingml.document"
                      className="form-input"
                      onChange={e => setCvFile(e.target.files?.[0] || null)}
                    />
                  </div>
                </div>
                <div className="modal-actions">
                  <button type="button" className="btn-ghost" onClick={() => setSelected(null)} disabled={sending}>Cancel</button>
                  <button type="button" className="btn-gold" onClick={handleApply} disabled={sending || !form.name || !form.email}>
                    {sending ? 'Submitting…' : 'Submit Application'}
                  </button>
                </div>
              </>
            )}
          </div>
        </div>
      )}
    </div>
  );
}
